<template>
	<div>
		<CEntryViewSettingsDialog
			v-if="!loading"
			:show="show"
			:view="view"
			:roles="viewFormData.Roles"
			:entry-views="entryViews"
			:is-multi-page-form="viewFormData.IsMultiPageForm"
			:org-code="orgCode"
			:email-role-assignments="viewFormData.EmailRoleAssignments"
			:workflow-links-enabled="viewFormData.WorkflowLinksEnabled"
			:is-org-owner="isOrgOwner"
			:show-tasks="showTasks"
			:rename-view="renameView"
			:can-copy="false"
			:can-delete="false"
			:field-infos="fieldInfos"
			:culture="formCulture"
			:organization-name="organizationName"
			:default-reply-to-email="organizationDefaultReplyToEmail"
			:tokens="tokens.filter(token => !token.IsProtected)"
			:form-has-logo="hasLogo"
			:is-payment-form="isPaymentForm"
			:save-and-resume-enabled="saveAndResumeEnabled"
			:is-task-reminders-enabled="!isVerified ? false : null"
			:default-workflow-link-button-text="organizationDefaultWorkflowLinkButtonText"
			:guest-portal-enabled="guestPortalEnabled"
			:can-manage-form="canManageForm"
			:can-manage-entries="canManageEntries"
			@save-view="handleSaveView"
			@closed="$emit('closed', $event)"
		/>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { computed, defineComponent, ref, watch } from 'vue';
	import CEntryViewSettingsDialog from 'src/views/entries/EntryViewSettingsDialog.vue';
	import type { EntryView } from '@cognitoforms/types/server-types/forms/model/entry-view';
	import { getEntryView, getEntryViewsFormData, updateEntryView } from '@cognitoforms/api/services/entry-view-service';
	import type { EntryViewsFormData } from '@cognitoforms/types/server-types/forms/model/entry-views-form-data';
	import type { UserEntryViewMeta } from '@cognitoforms/types/server-types/core/services/user-entry-view-meta';
	import cognitoMessenger from 'src/utilities/cognito-messaging';
	import { useViewSettingsDialogDependencies } from './use-view-settings-dialog-dependencies';
	import { globalStore } from 'src/stores/global-store';

	export default defineComponent({
		name: 'CViewSettingsDialogContainer',
		components: {
			CEntryViewSettingsDialog
		},
		props: {
			show: { type: Boolean, required: true },
			viewId: { type: String, required: true },
			orgCode: { type: String, required: true },
			isOrgOwner: { type: Boolean, default: false },
			showTasks: { type: Boolean, default: false },
			entryViews: { type: Array as PropType<UserEntryViewMeta[]>, required: true },
			renameView: { type: Boolean, default: false }
		},
		emits: ['closed'],
		setup(props, { emit }) {
			const loading = ref(true);

			const view = ref<EntryView>(null);
			const viewFormData = ref<EntryViewsFormData>(null);

			const handleSaveView = (viewToSave) =>{
				const formId = props.viewId.split('-')[0];

				const currentEntriesPageView = window.Cognito?.serialize(window.Cognito?.Forms.model.currentView);
				if (viewToSave.Id === currentEntriesPageView?.Id)
					cognitoMessenger.trigger('saveView', { data: viewToSave });
				else
					updateEntryView(formId, viewToSave.Id, viewToSave as EntryView);
				emit('closed');
			};

			const formId = computed(() => props.viewId?.split('-')[0]);

			const {
				fieldInfos,
				formCulture,
				organizationName,
				organizationDefaultReplyToEmail,
				organizationDefaultWorkflowLinkButtonText,
				tokens,
				hasLogo,
				isPaymentForm,
				isVerified,
				saveAndResumeEnabled,
				resolveViewSettingsDialogDependencies
			} = useViewSettingsDialogDependencies(formId.value);

			watch (() => props.show, async (show) =>{
				if (!show)
					return;

				loading.value = true;

				await resolveViewSettingsDialogDependencies();

				const [formId, viewId] = props.viewId.split('-');

				// If viewing settings dialog for current view, use currentView to get up to date version of the view (clientside changes i.e. filters)
				if (window.Cognito?.Forms.model.currentView?.get_Id() === props.viewId)
					view.value = window.Cognito?.serialize(window.Cognito?.Forms.model.currentView);
				else
					view.value = (await getEntryView(formId, viewId)).view;

				viewFormData.value = (await getEntryViewsFormData(formId));

				loading.value = false;
			}, { immediate: true });

			const guestPortalEnabled = computed(() => globalStore.portal.GuestPortalEnabled);
			const canManageForm = computed(() => globalStore.forms[formId.value]?.CanManageForm);
			const canManageEntries = computed(() => globalStore.forms[formId.value]?.CanManageEntries);

			return {
				loading,
				view,
				viewFormData,
				fieldInfos,
				formCulture,
				organizationName,
				organizationDefaultReplyToEmail,
				organizationDefaultWorkflowLinkButtonText,
				tokens,
				hasLogo,
				isPaymentForm,
				isVerified,
				handleSaveView,
				guestPortalEnabled,
				saveAndResumeEnabled,
				canManageForm,
				canManageEntries
			};
		}

	});
</script>
